import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Briefcase, Plus, Calendar as CalendarIcon, FileText, GraduationCap, Clock,
  CheckCircle, AlertTriangle, User, Shield, Download, Eye, RefreshCw,
  CalendarDays, FileCheck, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

export default function MyHRPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [myTraining, setMyTraining] = useState([]);
  const [myCompliance, setMyCompliance] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchMyData();
  }, []);

  const fetchMyData = async () => {
    try {
      setLoading(true);
      const [leaveRes, trainingRes, complianceRes, policiesRes] = await Promise.all([
        api.get('/hr/my-leave').catch(() => ({ data: [] })),
        api.get('/hr/my-training').catch(() => ({ data: [] })),
        api.get('/hr/my-compliance').catch(() => ({ data: [] })),
        api.get('/hr/policies').catch(() => ({ data: [] }))
      ]);
      setMyLeaveRequests(leaveRes.data || []);
      setMyTraining(trainingRes.data || []);
      setMyCompliance(complianceRes.data || []);
      setPolicies(policiesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch HR data:', error);
      toast.error('Failed to load your HR information');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/leave-requests', {
        ...leaveForm,
        staff_id: user.id
      });
      toast.success('Leave request submitted successfully');
      setLeaveDialogOpen(false);
      setLeaveForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      fetchMyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
      valid: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>{status}</Badge>;
  };

  const leaveBalance = {
    annual: 25,
    sick: 10,
    used: myLeaveRequests.filter(l => l.status === 'approved').length * 1
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-hr-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Briefcase className="w-8 h-8" />
            My HR & Workforce
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your leave, view training records, and access company policies
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Leave Balance</p>
                <p className="text-3xl font-bold text-green-600">{leaveBalance.annual - leaveBalance.used}</p>
                <p className="text-xs text-slate-400">days remaining</p>
              </div>
              <CalendarDays className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending Requests</p>
                <p className="text-3xl font-bold text-amber-600">
                  {myLeaveRequests.filter(l => l.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Training Completed</p>
                <p className="text-3xl font-bold text-blue-600">{myTraining.length}</p>
              </div>
              <GraduationCap className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Compliance Items</p>
                <p className="text-3xl font-bold text-purple-600">{myCompliance.length}</p>
              </div>
              <FileCheck className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="leave" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="leave">
            <CalendarIcon className="w-4 h-4 mr-2" />
            My Leave
          </TabsTrigger>
          <TabsTrigger value="training">
            <GraduationCap className="w-4 h-4 mr-2" />
            Training
          </TabsTrigger>
          <TabsTrigger value="compliance">
            <Shield className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="policies">
            <BookOpen className="w-4 h-4 mr-2" />
            Policies
          </TabsTrigger>
        </TabsList>

        {/* Leave Tab */}
        <TabsContent value="leave">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>My Leave Requests</CardTitle>
                  <CardDescription>View and request time off</CardDescription>
                </div>
                <Button onClick={() => setLeaveDialogOpen(true)} className="bg-[#1e3a5f]">
                  <Plus className="w-4 h-4 mr-2" />
                  Request Leave
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {myLeaveRequests.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No leave requests yet</p>
                  <p className="text-sm text-slate-400 mt-1">Click "Request Leave" to submit a new request</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myLeaveRequests.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="capitalize">{leave.leave_type?.replace('_', ' ')}</TableCell>
                        <TableCell>{new Date(leave.start_date).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell>{new Date(leave.end_date).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{leave.reason || '-'}</TableCell>
                        <TableCell>{getStatusBadge(leave.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training">
          <Card>
            <CardHeader>
              <CardTitle>My Training Records</CardTitle>
              <CardDescription>View your completed training and certifications</CardDescription>
            </CardHeader>
            <CardContent>
              {myTraining.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No training records found</p>
                  <p className="text-sm text-slate-400 mt-1">Your completed training will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Training Name</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Completion Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myTraining.map((training) => (
                      <TableRow key={training.id}>
                        <TableCell className="font-medium">{training.training_name}</TableCell>
                        <TableCell>{training.provider || '-'}</TableCell>
                        <TableCell>{training.completion_date ? new Date(training.completion_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>{training.expiry_date ? new Date(training.expiry_date).toLocaleDateString('en-GB') : 'No expiry'}</TableCell>
                        <TableCell>{getStatusBadge(training.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle>My Compliance Records</CardTitle>
              <CardDescription>DBS checks, right to work, and other compliance items</CardDescription>
            </CardHeader>
            <CardContent>
              {myCompliance.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No compliance records found</p>
                  <p className="text-sm text-slate-400 mt-1">Your compliance documents will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document Type</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myCompliance.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.document_type}</TableCell>
                        <TableCell>{item.reference_number || '-'}</TableCell>
                        <TableCell>{item.issue_date ? new Date(item.issue_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-GB') : 'No expiry'}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <CardTitle>Company Policies & Documents</CardTitle>
              <CardDescription>Access HR policies, handbooks, and important documents</CardDescription>
            </CardHeader>
            <CardContent>
              {policies.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No policies available</p>
                  <p className="text-sm text-slate-400 mt-1">Company policies will appear here when added</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {policies.map((policy) => (
                    <Card key={policy.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedPolicy(policy)}>
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-8 h-8 text-[#1e3a5f]" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{policy.title}</h3>
                            <p className="text-sm text-slate-500 capitalize">{policy.category?.replace('_', ' ')}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">v{policy.version}</Badge>
                              {policy.effective_date && (
                                <span className="text-xs text-slate-400">
                                  Effective: {new Date(policy.effective_date).toLocaleDateString('en-GB')}
                                </span>
                              )}
                            </div>
                          </div>
                          <Eye className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Leave Request Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLeaveSubmit} className="space-y-4">
            <div>
              <Label>Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm(prev => ({ ...prev, leave_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  <SelectItem value="compassionate">Compassionate Leave</SelectItem>
                  <SelectItem value="maternity">Maternity Leave</SelectItem>
                  <SelectItem value="paternity">Paternity Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea 
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Briefly describe the reason for your leave..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Submit Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Policy Viewer Dialog */}
      <Dialog open={!!selectedPolicy} onOpenChange={() => setSelectedPolicy(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPolicy?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <Badge variant="outline">v{selectedPolicy?.version}</Badge>
              <span className="capitalize">{selectedPolicy?.category?.replace('_', ' ')}</span>
              {selectedPolicy?.effective_date && (
                <span>Effective: {new Date(selectedPolicy.effective_date).toLocaleDateString('en-GB')}</span>
              )}
            </div>
            <div className="prose prose-slate max-w-none">
              <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                {selectedPolicy?.content || 'No content available.'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPolicy(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
